import {
  EAssetAutomationLevel,
  EAssetSource,
  EAssetType,
  evaluateAssetAutomationLevel,
  getRefId,
  type FindexInvestment,
  type FullInvestment,
  type ICompanyProfile,
  type ICryptoQuote,
  type ILiability,
} from 'findef';

type ICryptoQuoteLike = Partial<ICryptoQuote> & { _id?: string };
type ICompanyProfileLike = Partial<ICompanyProfile> & { _id?: string };
import type {
  UseAssetFormComputedData,
  UseAssetFormExternalData,
  UseAssetFormLayout,
  UseAssetFormState,
} from './types';
import { useMemoAsync } from '@/utils/async/useMemoAsync';
import { FindexClient } from '@/integrations/findex';
import { computed, ref, watch, type MaybeRef } from 'vue';
import { companyProfileField } from './fields/companyProfile';
import { parentIdField } from './fields/parentId';
import countries from '@/data/countries.json';
import type { DataFormFieldOption } from 'components/data-form/attributes';
import { cryptoQuoteField } from './fields/cryptoQuote';
import { commodityQuoteField } from './fields/commodityQuote';
import { defineField, defineGroup, useForm, Validators, type UseForm } from '@/form/useForm';
import type { ComputedRef, Ref } from 'vue';
import IRClient from '@/modules/ir/apiClient';
import { omit } from '@/utils/object';
import { pushToast } from 'components/toast/hooks';
import { refreshInvestments } from '~/composables/useDataRefresh';
import { useAnimationStep } from '@/utils/animation/useAnimationStep';
import { unlistedCompanyProfileField } from './fields/unlistedCompanyProfile';
import { investedField } from './fields/invested';
import { quantityField } from './fields/quantity';
import { timeInvestedField } from './fields/time';
import { symbolField } from './fields/symbol';
import { imageField } from './fields/image';
import { nameField } from './fields/name';
import { organizationNumberField } from './fields/organizationNumber';
import { ledgerField } from './fields/ledger';
import { subtypesField } from './fields/subtypes';
import { useUserSettings } from '@/modules/settings/hooks/useUserSettings';
import { useForexTickers } from '@/integrations/findex/hooks/forex/useForexTickers';
import { computeRoi } from '@/utils/roi';
import { ownershipPercentageField } from './fields/ownershipPercentage';
import { liabilityToOption } from '@/form/options/liability';
import { twColor } from '@/utils/style';
import { pushQuickAddLiabilityModal } from '@/modules/liability/modals/quick-add-liability';
import { selectAssetTypeField } from './fields/selectAssetType';

const countryOptions = countries.map(
  (country): DataFormFieldOption => ({
    label: country.name,
    value: country.code2 || country.code3 || country.name,
    iconLeft: {
      src: async () => import(`assets/img/svg/flags/4x3/${country.code2.toLowerCase()}.svg?raw`),
      size: 14,
      cacheKey: `assets/img/svg/flags/4x3/${country.code2.toLowerCase()}.svg?raw`,
    },
  }),
);

export type UseAssetForm = {
  form: UseForm<UseAssetFormState, UseAssetFormComputedData, UseAssetFormExternalData>;
  investment?: FullInvestment;
  isDeleting: Ref<boolean>;
  save: () => Promise<FindexInvestment | null>;
  deleteInvestment: () => Promise<void>;
  loadingProgress: ComputedRef<number>;
  isLoading: Ref<boolean>;
  isSaving: Ref<boolean>;
  onFinsquidComplete?: () => any;
};
export type UseAssetFormProps = {
  investmentId?: string;
  investment?: FullInvestment;
  layout?: MaybeRef<UseAssetFormLayout>;
  onDeleted?: () => any;
  currentLiability?: Ref<Omit<ILiability, 'user'>>;
};

export type AssetForm = UseForm<
  UseAssetFormState,
  UseAssetFormComputedData,
  UseAssetFormExternalData
>;

export const useAssetForm = (props: UseAssetFormProps): UseAssetForm => {
  const isDeleting = ref<boolean>(false);
  const isLoading = ref<boolean>(true);
  const isSaving = ref<boolean>(false);
  const anim = useAnimationStep({
    initial: 0,
    duration: 1.0,
  });
  anim.to(0, 0);
  const loadingProgress = computed(() => anim.current.value);

  const form = useForm<UseAssetFormState, UseAssetFormComputedData, UseAssetFormExternalData>({
    schema: {
      _id: defineField({
        value: '',
      }),
      hasLiabilitiesAttached: defineField({
        type: 'boolean',
        value: false,
        component: 'switch',
        label: 'Liabilities Attached',
        subLabel: 'Toggle if this asset is financed with liabilities',
        inlineLabel: true,
        disabled: !!props.currentLiability,
      }),
      liabilities: defineField({
        type: 'set',
        value: [],
        component: 'multi-select',
        placeholder: 'Select from portfolio',
        hidden: !!props.currentLiability,
        injectedProps: {
          icon: {
            src: async () => import('assets/img/svg/icons/receipt.svg?raw'),
            size: '1rem',
            stroke: 'none',
            fill: twColor('gray-400'),
          },
          manuallyAddAlwaysVisible: true,
          manuallyAddText: 'Quick Add Liability',
          itemHeight: 56,
          itemSingularName: 'liability',
          itemPluralName: 'liabilities',
        },
        options: {
          add: {
            createItem: async (name) => {
              const liab = await pushQuickAddLiabilityModal({
                liabilityName: name,
                currentInvestment: form.state,
              });
              if (liab) {
                return liabilityToOption(liab);
              }
            },
          },
          search: {
            enabled: true,
            api: {
              fetchAll: async () => {
                const [liabs] = await FindexClient.getLiabilities({});
                return (liabs || []).map((it) => liabilityToOption(it));
              },
              fetch: async (queryString) => {
                const [liabs] = await FindexClient.getLiabilities({ queryString });
                return (liabs || []).map((it) => liabilityToOption(it));
              },
              fetchSuggestions: async () => {
                const [liabs] = await FindexClient.getLiabilities({});
                return (liabs || []).map((it) => liabilityToOption(it));
              },
            },
          },
        },
      }),
      manualTicker: defineField({
        type: 'boolean',
        value: false,
        needs: ['asset.cryptoQuote', 'asset.companyProfile', 'asset.commodityQuote'],
        next: (state) => {
          const manual =
            state.asset.cryptoQuote?.manuallyAdded ||
            state.asset.companyProfile?.manuallyAdded ||
            state.asset.commodityQuote?.manuallyAdded ||
            false;
          return {
            value: manual,
          };
        },
      }),
      currentValue: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Amount Invested',
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
            needs: [
              'asset.type',
              'asset.ledger.sharePrice.type',
              'invested.value',
              'asset.ledger.sharePrice.value',
              'invested.type',
              'quantity',
            ],
            next: (state, _data) => {
              return {
                value: state.quantity * state.asset.ledger.sharePrice.value,
              };
            },
          }),
          type: defineField({
            type: 'text',
            value: 'SEK',
            component: 'selectbox',
            needs: ['invested.type'],
            next: (state, _data) => {
              return {
                value: state.invested.type,
              };
            },
          }),
        },
      }),
      acquiredPrice: defineGroup({
        attributes: {
          class: 'grid grid-cols-[auto_7rem]',
          label: 'Amount Invested',
        },
        fields: {
          value: defineField({
            type: 'number',
            value: 0,
            placeholder: '0',
          }),
          type: defineField({
            type: 'text',
            value: 'SEK',
            component: 'selectbox',
          }),
        },
      }),
      userDoesNotWantImage: defineField({ type: 'boolean', value: false }),
      image: imageField({
        onRemove: () => {
          queueMicrotask(() => {
            form.setFieldValue('userDoesNotWantImage', true);
            form.setFieldValue('image', undefined);
          });
        },
        onUpload: () => {
          queueMicrotask(() => {
            form.setFieldValue('userDoesNotWantImage', false);
          });
        },
      }),
      providerImport: defineField({
        type: 'dbObject',
      }),
      provider: defineField({
        type: 'dbObject',
      }),
      invested: investedField(),
      quantity: quantityField(),
      ownershipPercentage: ownershipPercentageField(),
      time: timeInvestedField(),
      parentId: parentIdField(),
      originalParentId: defineField({ type: 'string', value: '' }),
      symbol: symbolField(),
      automation: defineField({
        type: 'string',
        value: EAssetAutomationLevel.MANUAL,
        needs: ['asset.automation'],
        next: (state) => ({
          value: state.asset.automation,
        }),
      }),
      asset: defineGroup({
        fields: {
          provider: defineField({ type: 'dbObject' }),
          automation: defineField({
            type: 'string',
            value: EAssetAutomationLevel.MANUAL,
            needs: ['asset.cryptoQuote', 'asset.companyProfile', 'asset.commodityQuote'],
            next: (state) => ({
              value: evaluateAssetAutomationLevel(state.asset),
            }),
          }),
          source: defineField({
            type: 'string',
            value: EAssetSource.MANUAL,
            needs: ['asset.type', 'asset.automation'],
          }),
          type: defineField({
            type: 'string',
            value: EAssetType.EQUITY,
          }),
          name: nameField(),
          symbol: defineField({
            label: 'Symbol',
            type: 'text',
          }),
          organizationNumber: organizationNumberField(),
          image: defineField({
            type: 'file',
            hooks: {
              image: {
                fun: (state) => {
                  return {
                    ...(state.image
                      ? {
                        value: state.image,
                      }
                      : {
                        value: state.asset.image,
                      }),
                  };
                },
              },
            },
            needs: [
              'asset.cryptoQuote',
              'asset.commodityQuote',
              'asset.companyProfile',
              'userDoesNotWantImage',
            ],
            next: (state) => {
              if (state.userDoesNotWantImage === true)
                return {
                  value: undefined,
                };
              const crypto = state.asset.cryptoQuote;
              if (crypto && crypto.image) return { value: crypto.image };
              const company = state.asset.companyProfile;
              if (company && company.image) return { value: company.image };

              return {
                value: state.asset.image || state.image,
              };
            },
          }),
          ledger: ledgerField(),
          companyProfile: defineField<UseAssetFormState, any, UseAssetFormComputedData>({
            type: 'dbObject',
            needs: ['asset.listed', 'isEditMode'],
            next: (state) => {
              const opts = state.asset.listed
                ? companyProfileField(true)
                : unlistedCompanyProfileField(false);

              const { hooks: _hooks, next: _next, ...rest } = opts;

              return {
                ...rest,
                locked: state.isEditMode === true,
              };
            },
          }),
          cryptoQuote: cryptoQuoteField(),
          commodityQuote: commodityQuoteField(),
          subtypes: subtypesField(),
          interest: defineField({
            type: 'number',
            label: 'Yearly Interest',
            value: 0,
            placeholder: '0',
            display: { value: '%' },
            validators: [Validators.min(0)],
          }),
          contactEmail: defineField({
            type: 'text',
          }),
          listed: defineField({ type: 'boolean' }),
          address: defineField({
            label: 'Address',
            placeholder: 'Enter address',
            type: 'text',
          }),
          zip: defineField({
            label: 'Postal Code',
            placeholder: 'Enter postal code',
            type: 'text',
          }),
          city: defineField({
            label: 'City',
            placeholder: 'Enter city',
            type: 'text',
          }),
          country: defineField({
            type: 'text',
            component: 'select-dropdown',
            label: 'Country',
            value: 'SE',
            placeholder: 'Select country',
            options: {
              items: countryOptions,
              search: {
                enabled: true,
              },
            },
          }),
        },
      }),
      ownedByCompany: defineField({
        type: 'boolean',
        label: 'Owned by Company',
        inlineLabel: true,
        subLabel:
          'Toggle if this asset is owned by e.g. a holding company and not directly by you.',
        value: false,
        needs: ['parentId', 'asset.type'],
        next: (state) => ({
          value: !!state.parentId && state.parentId !== '',
        }),
      }),
      selectedAssetType: selectAssetTypeField({
        assetType: EAssetType.EQUITY,
        listed: true,
      }),
      isEditMode: defineField({
        type: 'boolean',
        value: !!props.investmentId,
      }),
    },
    layout: props.layout || {
      children: [],
    },
    computed: [
      {
        needs: ['invested.type', 'asset.ledger.sharePrice.type'],
        next: (state) => {
          const settings = useUserSettings();
          const forex = useForexTickers();
          const tickerInitial = forex.getTickerRef(
            state?.invested.type || settings.currency.value,
            settings.currency.value,
          );
          const sharePriceTicker = forex.getTickerRef(
            state?.asset.ledger.sharePrice.type || settings.currency.value,
            settings.currency.value,
          );

          const calculateYearsSince = (date: Date): number => {
            const now = new Date();
            const diffTime = now.getTime() - date.getTime();
            const yearsDiff = diffTime / (1000 * 60 * 60 * 24 * 365.25);
            return yearsDiff < 1 / 365.25 ? 0 : yearsDiff;
          };

          return (state) => {
            const invested =
              state.value.asset.type === EAssetType.REAL_ESTATE
                ? (state.value.invested.value || 0) * state.value.quantity
                : state.value.invested.value || 0;
            const shares =
              state.value.asset.type === EAssetType.CASH ? 1 : state.value.quantity || 0;
            const latestSharePrice =
              state.value.asset.type === EAssetType.CASH
                ? invested
                : (state.value.asset.ledger.sharePrice.value || 0) * sharePriceTicker.value.bid;
            const interest = state.value.asset.interest || 0;
            const investmentDate = state.value.time || new Date();

            let initialValue = invested;
            let currentValue: number;
            if ((state.value.asset.interest || 0) > 0) {
              // Calculate compound interest: P(1 + r)^t
              // where P is principal (initial value), r is interest rate (as decimal), t is time in years
              const years = calculateYearsSince(investmentDate);
              const interestRate = (state.value.asset.interest || interest) * 0.01; // Convert percentage to decimal
              currentValue = initialValue * Math.pow(1 + interestRate, years);
            } else {
              currentValue = shares * latestSharePrice;
            }
            initialValue = initialValue * tickerInitial.value.bid;

            //if (state.value.asset.type === EAssetType.CASH) {
            //  currentValue = currentValue * tickerInitial.value.bid;
            //}

            return {
              ROI: computeRoi(initialValue, currentValue),
              ROICash: computeRoi(initialValue, currentValue * tickerInitial.value.bid),
              currentValue: currentValue,
              currentValueCash: currentValue * tickerInitial.value.bid,
              initialValue: initialValue,
            };
          };
        },
      },
    ],
    externalData: {
      currentLiability: props.currentLiability,
    },
  });

  const investId = computed((): string | null =>
    props.investmentId ? props.investmentId : props.investment ? getRefId(props.investment) : null,
  );

  const investmentQuery = useMemoAsync(async () => {
    const invId = investId.value;
    if (!invId) return null;
    const [inv] = await FindexClient.getInvestmentById(invId);
    if (!inv) return null;
    return inv as FullInvestment;
  });

  const initialState = computed((): UseAssetFormState | null => {
    if (!investmentQuery.data) return null;
    const data = investmentQuery.data;
    return {
      ...data,
      quantity: (data?.asset?.type === EAssetType.REAL_ESTATE ? ((data?.quantity || 0) * 100) : data?.quantity) || 0,
      ownershipPercentage: (data?.quantity || 0) * 100,
      invested: {
        ...data.invested,
        value:
          data.asset.type === EAssetType.REAL_ESTATE
            ? data.invested.value / data.quantity
            : data.invested.value,
      },
      hasLiabilitiesAttached: Array.isArray(data.liabilities) && data.liabilities.length > 0,
    };
  });

  form.watchState(initialState, () => {
    form.setFieldValue('isEditMode', true, true);
    form.setFieldValue(
      'originalParentId',
      initialState.value?.parentId ? getRefId(initialState.value?.parentId) : '',
    );
    anim.to(1.0, 0.5).then(() => {
      anim.to(0.0, 0.01);
      isLoading.value = false;
    });
  });

  const saveNewAsset = async (data: UseAssetFormState) => {
    try {
      const asset = await IRClient.createAsset(omit(data.asset, ['parentId']));
      const initialValue = form.computedData.value.initialValue;
      const currentValue = form.computedData.value.currentValue;
      const inv = await IRClient.investInAsset(getRefId(asset), {
        ...data,
        invested: {
          ...data.invested,
          value: data.invested.value ?? initialValue,
        },
        currentValue: {
          ...data.currentValue,
          value:
            asset.type === EAssetType.CASH ||
              (asset.type === EAssetType.EQUITY && asset.listed) ||
              asset.type === EAssetType.COMMODITY ||
              asset.type === EAssetType.CRYPTO
              ? currentValue
              : data.currentValue?.value ?? currentValue,
        },
      });
      pushToast({
        title: 'Asset Created',
        subtitle: `${data.asset.name} was created and added to your portfolio`,
        variant: 'success',
      });
      return inv;
    } catch (e) {
      console.error(e);
      pushToast({
        title: 'Error',
        subtitle: 'Failed to create asset, please try again later',
        variant: 'error',
      });
      return null;
    }
  };

  const saveEdits = async (data: UseAssetFormState, investmentId: string) => {
    if (!investmentId) {
      console.error(`No investment id`);
      return null;
    }

    try {
      const initialValue = form.computedData.value.initialValue;
      const currentValue = form.computedData.value.currentValue;
      console.log(form.computedData.value.currentValue);

      // Extract the name from asset.name and save it to investment.name instead
      const investmentName = data.asset.name;

      const inv = await IRClient.editInvestment(investmentId, {
        ...data,
        name: investmentName,  // Save custom name to investment
        invested: {
          ...data.invested,
          value: initialValue,
        },
        currentValue: {
          ...data.currentValue,
          value: currentValue,
        },
      });
      pushToast({
        title: 'Changes Saved',
        subtitle: `Your edits for ${investmentName || data.asset.name} was saved`,
        variant: 'success',
      });
      return inv;
    } catch (e) {
      console.error(e);
      pushToast({
        title: 'Error',
        subtitle: 'Failed to save changes, please try again later',
        variant: 'error',
      });
      return null;
    }
  };

  const save = async () => {
    isSaving.value = true;
    isLoading.value = true;
    anim.to(0.25, 1.0);
    const data = form.cast();
    anim.to(0.5, 0.5);

    let inv: FindexInvestment | null = null;
    if (props.investmentId) {
      await saveEdits(data, props.investmentId);
      const [resp] = await FindexClient.getInvestmentById(props.investmentId);
      if (resp) {
        inv = resp;
      }
    } else {
      inv = await saveNewAsset(data);
      if (inv) {
        const [resp] = await FindexClient.getInvestmentById(getRefId(inv));
        if (resp) {
          inv = resp;
        }
      }
    }
    // Bump the shared investmentsRefreshCount so portfolio/dashboard lists
    // refetch. Without this, an edited logo (or any other field) stays stale
    // in cached views until the user navigates away and back.
    if (inv) refreshInvestments();
    await anim.to(1.0, 0.5);
    anim.to(0.0, 0.01);
    isLoading.value = false;
    isSaving.value = false;
    return inv;
  };

  const deleteInvestment = async () => {
    const id = props.investmentId;
    if (!id) {
      console.warn(`Missing investment id.`);
      return;
    }
    isDeleting.value = true;
    await anim.to(0.25, 1.0);
    try {
      await IRClient.deleteInvestment(id);
    } catch (e) {
      console.error(e);
      isDeleting.value = true;
      throw e;
    }
    await anim.to(0.75, 0.5);
    await anim.to(1.0, 1.0);
    anim.to(0.0, 0.01);
    isDeleting.value = false;

    if (props.onDeleted) {
      await props.onDeleted();
    }
  };

  // Eagerly resolve logos the moment the user picks a CryptoQuote or
  // CompanyProfile in step 1. The backend runs findLogo once, caches the
  // attachment on the source document, and returns the updated document so
  // we can drop it straight back into form state — which means the step-2
  // banner, the save loader, and everything downstream reads a real image
  // ref instead of a generic asset-type icon.
  const resolvedCryptoIds = new Set<string>();
  watch(
    () => form.state.value.asset.cryptoQuote,
    async (crypto) => {
      if (!crypto || typeof crypto !== 'object') return;
      if ((crypto as ICryptoQuoteLike).image) return;
      const id = getRefId(crypto as any);
      if (!id || resolvedCryptoIds.has(id)) return;
      resolvedCryptoIds.add(id);
      try {
        const [updated] = await FindexClient.ensureCryptoQuoteImage(id);
        if (updated?.image) {
          const current = form.state.value.asset.cryptoQuote as any;
          if (current && getRefId(current) === id) {
            form.setFieldValue('asset.cryptoQuote', updated as any);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    { immediate: true },
  );

  const resolvedProfileIds = new Set<string>();
  watch(
    () => form.state.value.asset.companyProfile,
    async (profile) => {
      if (!profile || typeof profile !== 'object') return;
      if ((profile as ICompanyProfileLike).image) return;
      if ((profile as ICompanyProfileLike).manuallyAdded) return;
      // Skip unlisted equities entirely — they shouldn't trigger scraping.
      if (form.state.value.asset.listed === false) return;
      const id = getRefId(profile as any);
      if (!id || resolvedProfileIds.has(id)) return;
      resolvedProfileIds.add(id);
      try {
        const [updated] = await FindexClient.ensureCompanyProfileImage(id);
        if (updated?.image) {
          const current = form.state.value.asset.companyProfile as any;
          if (current && getRefId(current) === id) {
            form.setFieldValue('asset.companyProfile', updated as any);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    { immediate: true },
  );

  return {
    form,
    investment: props.investment,
    isDeleting,
    save,
    deleteInvestment,
    isLoading,
    loadingProgress,
    isSaving,
  };
};
